import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { PageHeader, Section, Spinner, RiskBadge, StatusBadge } from "@/components/ui-parts";
import { X, ExternalLink, Filter, RotateCcw, Download, Upload, Paperclip, Trash2, Loader2, FileText, FileImage, FileSpreadsheet, FileArchive, FileCode, FileAudio, FileVideo, File as FileIcon, Eye } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const RISKS = ["Low", "Medium", "High", "Critical"];
const STATUSES = ["Not Assessed", "In Progress", "Assessed", "Accepted Risk"];
const SCORES = ["", "0", "1", "2", "3", "4", "5"];

const FW_FIELDS = [
  { id: "gacrf", label: "GACRF®" },
  { id: "iso27001", label: "ISO 27001" },
  { id: "nistCsf", label: "NIST CSF 2.0" },
  { id: "iec62443", label: "IEC 62443" },
  { id: "cisControls", label: "CIS v8" },
  { id: "mitreAttack", label: "MITRE ATT&CK" },
  { id: "capec", label: "CAPEC" },
  { id: "dora", label: "DORA" },
];

export default function RequirementsPage() {
  const { user } = useAuth();
  const { withScope, activeTenantId } = useTenant();
  const [rows, setRows] = useState([]);
  const [org, setOrg] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", domain: "", capability: "", risk: "", status: "", framework: "", controlId: "" });
  const [drawer, setDrawer] = useState(null);
  const canEdit = user?.role !== "viewer";

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/requirements", { params: withScope() }),
      api.get("/settings/organization", { params: withScope() }),
    ])
      .then(([r, o]) => { setRows(r.data); setOrg(o.data || {}); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTenantId]);

  const domains = useMemo(() => [...new Set(rows.map((r) => r.domain))].sort(), [rows]);
  const capabilities = useMemo(
    () => [...new Set(rows.filter((r) => !filters.domain || r.domain === filters.domain).map((r) => r.capability))].sort(),
    [rows, filters.domain]
  );

  // Active frameworks for this tenant (from Org Settings)
  const activeFrameworks = org.activeFrameworks?.length ? org.activeFrameworks : FW_FIELDS.map((f) => f.id);

  const filtered = useMemo(() => {
    const q = filters.q.toLowerCase();
    const ctrl = filters.controlId.toLowerCase().trim();
    return rows.filter((r) => {
      const blob = JSON.stringify(r).toLowerCase();
      if (q && !blob.includes(q)) return false;
      if (filters.domain && r.domain !== filters.domain) return false;
      if (filters.capability && r.capability !== filters.capability) return false;
      if (filters.risk && r.riskLevel !== filters.risk) return false;
      if (filters.status && r.status !== filters.status) return false;
      // Framework filter: only rows that have a mapping in the selected framework
      if (filters.framework) {
        const val = (r[filters.framework] || "").toString().toLowerCase();
        if (!val) return false;
        if (ctrl && !val.includes(ctrl)) return false;
      } else if (ctrl) {
        // Search across all framework columns
        const all = FW_FIELDS.map((f) => (r[f.id] || "").toString().toLowerCase()).join(" | ");
        if (!all.includes(ctrl)) return false;
      }
      return true;
    });
  }, [rows, filters]);

  const update = async (id, field, value) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    try {
      await api.patch(`/requirements/${id}`, { [field]: value });
    } catch (e) {
      alert(formatApiError(e));
      load();
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Bu tenant'ın tüm assessment kayıtları sıfırlanacak. Emin misiniz?")) return;
    try { await api.post("/requirements/reset", null, { params: withScope() }); load(); } catch (e) { alert(formatApiError(e)); }
  };

  const clearFilters = () => setFilters({ q: "", domain: "", capability: "", risk: "", status: "", framework: "", controlId: "" });

  const exportCsv = async () => {
    const params = new URLSearchParams();
    const scope = withScope();
    if (scope.tenant_id) params.set("tenant_id", scope.tenant_id);
    const res = await fetch(`${BASE}/api/requirements/csv${params.toString() ? "?" + params : ""}`, {
      credentials: "include",
      headers: localStorage.getItem("gacrf_token") ? { Authorization: `Bearer ${localStorage.getItem("gacrf_token")}` } : {},
    });
    if (!res.ok) return alert("CSV export failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `GACRF_requirements_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const importCsv = async (file) => {
    if (!file) return;
    const text = await file.text();
    const params = new URLSearchParams();
    const scope = withScope();
    if (scope.tenant_id) params.set("tenant_id", scope.tenant_id);
    const res = await fetch(`${BASE}/api/requirements/csv${params.toString() ? "?" + params : ""}`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "text/csv", ...(localStorage.getItem("gacrf_token") ? { Authorization: `Bearer ${localStorage.getItem("gacrf_token")}` } : {}) },
      body: text,
    });
    const result = await res.json().catch(() => ({}));
    if (!res.ok) return alert(result.detail || "CSV import failed");
    alert(`✓ Import tamamlandı\nUpdated: ${result.updated} · Missing IDs: ${result.missing_ids} · Skipped: ${result.skipped_rows}`);
    load();
  };

  return (
    <div className="p-8 max-w-[1900px]" data-testid="requirements-page">
      <PageHeader
        eyebrow="Assessment"
        title="Requirement Assessment"
        description="Evidence-based scoring. Filter by framework to focus the audit scope (ISO 27001 / NIST CSF / IEC 62443 / CIS v8 / MITRE ATT&CK / CAPEC / DORA)."
        actions={
          <>
            <button className="btn btn-secondary btn-sm" onClick={exportCsv} data-testid="csv-export-btn">
              <Download size={12} /> Export CSV
            </button>
            {(user?.role === "admin" || user?.role === "auditor" || user?.role === "superadmin") && (
              <label className="btn btn-secondary btn-sm cursor-pointer" data-testid="csv-import-btn">
                <Upload size={12} /> Import CSV
                <input type="file" accept=".csv" hidden onChange={(e) => { importCsv(e.target.files[0]); e.target.value = ""; }} />
              </label>
            )}
            {(user?.role === "admin" || user?.role === "superadmin") && (
              <button className="btn btn-secondary btn-sm" onClick={resetAll} data-testid="reset-btn">
                <RotateCcw size={12} /> Reset Data
              </button>
            )}
          </>
        }
      />

      {/* Filters */}
      <div className="card p-4 mb-4" data-testid="filters">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 items-end">
          <div>
            <label className="text-[9px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Search</label>
            <input placeholder="Any text..." value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="filter-search" />
          </div>
          <div>
            <label className="text-[9px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Framework Scope</label>
            <select value={filters.framework} onChange={(e) => setFilters({ ...filters, framework: e.target.value })} data-testid="filter-framework">
              <option value="">All Frameworks</option>
              {FW_FIELDS.filter((f) => activeFrameworks.includes(f.id)).map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[9px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Control ID</label>
            <input placeholder="e.g. A.5.1" value={filters.controlId} onChange={(e) => setFilters({ ...filters, controlId: e.target.value })} data-testid="filter-control" />
          </div>
          <div>
            <label className="text-[9px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Domain</label>
            <select value={filters.domain} onChange={(e) => setFilters({ ...filters, domain: e.target.value, capability: "" })} data-testid="filter-domain">
              <option value="">All</option>{domains.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[9px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Capability</label>
            <select value={filters.capability} onChange={(e) => setFilters({ ...filters, capability: e.target.value })} data-testid="filter-capability">
              <option value="">All</option>{capabilities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[9px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Risk</label>
            <select value={filters.risk} onChange={(e) => setFilters({ ...filters, risk: e.target.value })} data-testid="filter-risk">
              <option value="">All</option>{RISKS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[9px] font-mono uppercase tracking-[0.15em] text-slate-500 block mb-1">Status</label>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status">
              <option value="">All</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <button className="btn btn-ghost btn-sm justify-center" onClick={clearFilters} data-testid="filter-clear">
            <Filter size={12} /> Clear
          </button>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 mt-3 flex items-center gap-4">
          <span>Shown: <span className="text-slate-300">{filtered.length}</span> / {rows.length}</span>
          {filters.framework && (
            <span>Framework: <span className="text-[#00E5FF]">{FW_FIELDS.find((f) => f.id === filters.framework)?.label}</span></span>
          )}
        </div>
      </div>

      {/* Table */}
      <Section>
        {loading ? (
          <div className="p-8"><Spinner label="Loading requirements..." /></div>
        ) : (
          <div className="overflow-x-auto" style={{ maxHeight: "72vh" }}>
            <table className="data-table" data-testid="requirements-table">
              <thead>
                <tr>
                  <th></th><th>Req ID</th><th>Domain / Capability</th>
                  <th style={{ minWidth: 240 }}>Audit Question</th>
                  <th style={{ minWidth: 220 }}>Expected Evidence</th>
                  <th style={{ minWidth: 260 }}>Framework Crosswalk</th>
                  <th>Risk</th><th>Score</th><th>Status</th>
                  <th>Owner</th><th>Evidence</th><th>Gap</th><th>Improvement</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`req-row-${r.requirementId}`}>
                    <td>
                      <button className="btn btn-ghost btn-sm" onClick={() => setDrawer(r)} data-testid={`open-${r.requirementId}`}>
                        <ExternalLink size={12} />
                      </button>
                    </td>
                    <td className="mono">{r.requirementId}</td>
                    <td>
                      <div className="text-slate-200 text-xs font-semibold">{r.capability}</div>
                      <div className="text-slate-500 text-[10px]">{r.domain}</div>
                    </td>
                    <td className="text-slate-300 text-xs leading-relaxed" data-testid={`audit-${r.requirementId}`}>{r.auditQuestion}</td>
                    <td className="text-slate-400 text-xs leading-relaxed" data-testid={`evidence-exp-${r.requirementId}`}>{r.expectedEvidence}</td>
                    <td data-testid={`crosswalk-${r.requirementId}`}>
                      <div className="flex flex-col gap-1">
                        {FW_FIELDS.filter((f) => r[f.id]).map((f) => {
                          const isActive = activeFrameworks.includes(f.id);
                          return (
                            <div key={f.id} className={`text-[10px] font-mono flex items-baseline gap-2 ${isActive ? "" : "opacity-50"}`}>
                              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${isActive ? "bg-[#00E5FF]/10 text-[#00E5FF]" : "bg-slate-800 text-slate-500"}`}>{f.label}</span>
                              <span className="text-slate-300">{r[f.id]}</span>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      <select disabled={!canEdit} value={r.riskLevel || ""} onChange={(e) => update(r.id, "riskLevel", e.target.value)} data-testid={`risk-${r.requirementId}`}>
                        {RISKS.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </td>
                    <td className="score-cell">
                      <select disabled={!canEdit} value={r.score ?? ""} onChange={(e) => update(r.id, "score", e.target.value)} data-testid={`score-${r.requirementId}`}>
                        {SCORES.map((v) => <option key={v} value={v}>{v === "" ? "—" : v}</option>)}
                      </select>
                    </td>
                    <td>
                      <select disabled={!canEdit} value={r.status || "Not Assessed"} onChange={(e) => update(r.id, "status", e.target.value)} data-testid={`status-${r.requirementId}`}>
                        {STATUSES.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </td>
                    <td>
                      <input disabled={!canEdit} value={r.owner || ""} onChange={(e) => update(r.id, "owner", e.target.value)} placeholder="—" style={{ minWidth: 100 }} data-testid={`owner-${r.requirementId}`} />
                    </td>
                    <td>
                      <input disabled={!canEdit} value={r.evidenceLink || ""} onChange={(e) => update(r.id, "evidenceLink", e.target.value)} placeholder="link/ref" style={{ minWidth: 120 }} data-testid={`evlink-${r.requirementId}`} />
                    </td>
                    <td>
                      <textarea disabled={!canEdit} rows={2} value={r.gap || ""} onChange={(e) => update(r.id, "gap", e.target.value)} placeholder="gap..." style={{ minWidth: 160 }} data-testid={`gap-${r.requirementId}`} />
                    </td>
                    <td>
                      <textarea disabled={!canEdit} rows={2} value={r.improvement || ""} onChange={(e) => update(r.id, "improvement", e.target.value)} placeholder="action..." style={{ minWidth: 160 }} data-testid={`improvement-${r.requirementId}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {drawer && <RequirementDrawer req={drawer} onClose={() => setDrawer(null)} activeFrameworks={activeFrameworks} canEdit={canEdit} />}
    </div>
  );
}

function RequirementDrawer({ req, onClose, activeFrameworks, canEdit }) {
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer" data-testid="requirement-drawer">
        <div className="p-5 border-b border-slate-800/70 flex items-center justify-between sticky top-0 bg-[#0F172A] z-10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Requirement Detail</div>
            <div className="font-heading text-lg font-bold mt-0.5">{req.requirementId}</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} data-testid="drawer-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Domain" value={req.domain} />
          <Field label="Capability" value={req.capability} />
          <Field label="Requirement" value={req.requirement} />
          <Field label="Audit Question" value={req.auditQuestion} />
          <Field label="Expected Evidence" value={req.expectedEvidence} />

          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 mb-2">Framework Crosswalk</div>
            <div className="card p-3 space-y-2">
              {FW_FIELDS.filter((f) => req[f.id]).map((f) => {
                const isActive = activeFrameworks?.includes(f.id);
                return (
                  <div key={f.id} className="grid grid-cols-3 gap-2 items-baseline text-xs">
                    <div className={`font-mono text-[10px] uppercase tracking-wider ${isActive ? "text-[#00E5FF]" : "text-slate-500"}`}>{f.label}{isActive && " ●"}</div>
                    <div className="col-span-2 font-mono text-slate-200">{req[f.id]}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <EvidenceFilesPanel requirement={req} canEdit={canEdit} />

          <Field label="RACI" value={req.raci} />
          <Field label="KPI" value={req.kpi} />
          <Field label="KRI" value={req.kri} />
          <div className="grid grid-cols-2 gap-3">
            <div className="card p-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 mb-1">Risk Level</div>
              <RiskBadge level={req.riskLevel} />
            </div>
            <div className="card p-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 mb-1">Status</div>
              <StatusBadge status={req.status || "Not Assessed"} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB

function humanSize(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fileTypeMeta(filename = "", contentType = "") {
  const ext = (filename.split(".").pop() || "").toLowerCase();
  const ct = (contentType || "").toLowerCase();
  if (ct.startsWith("image/") || ["png","jpg","jpeg","gif","webp","svg","bmp","heic"].includes(ext)) {
    return { Icon: FileImage, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30", label: "IMAGE" };
  }
  if (ct === "application/pdf" || ext === "pdf") {
    return { Icon: FileText, color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30", label: "PDF" };
  }
  if (ct.includes("spreadsheet") || ct === "text/csv" || ["xls","xlsx","csv","ods","tsv"].includes(ext)) {
    return { Icon: FileSpreadsheet, color: "text-emerald-300", bg: "bg-emerald-600/10", border: "border-emerald-600/30", label: "SHEET" };
  }
  if (ct.includes("wordprocessing") || ct.includes("msword") || ["doc","docx","rtf","odt"].includes(ext)) {
    return { Icon: FileText, color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30", label: "DOC" };
  }
  if (ct.includes("presentation") || ["ppt","pptx","odp"].includes(ext)) {
    return { Icon: FileText, color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/30", label: "SLIDES" };
  }
  if (ct.includes("zip") || ct.includes("compressed") || ["zip","rar","7z","tar","gz","bz2"].includes(ext)) {
    return { Icon: FileArchive, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30", label: "ARCHIVE" };
  }
  if (ct.startsWith("audio/") || ["mp3","wav","ogg","flac","aac","m4a"].includes(ext)) {
    return { Icon: FileAudio, color: "text-fuchsia-400", bg: "bg-fuchsia-500/10", border: "border-fuchsia-500/30", label: "AUDIO" };
  }
  if (ct.startsWith("video/") || ["mp4","mov","avi","mkv","webm","flv"].includes(ext)) {
    return { Icon: FileVideo, color: "text-pink-400", bg: "bg-pink-500/10", border: "border-pink-500/30", label: "VIDEO" };
  }
  if (["js","ts","jsx","tsx","py","java","c","cpp","cs","go","rs","rb","php","json","xml","yaml","yml","html","css","sh"].includes(ext)) {
    return { Icon: FileCode, color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/30", label: "CODE" };
  }
  if (ct === "text/plain" || ["txt","md","log"].includes(ext)) {
    return { Icon: FileText, color: "text-slate-300", bg: "bg-slate-500/10", border: "border-slate-500/30", label: "TEXT" };
  }
  return { Icon: FileIcon, color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/30", label: (ext || "FILE").toUpperCase() };
}

function FileTypeBadge({ filename, contentType }) {
  const meta = fileTypeMeta(filename, contentType);
  const Icon = meta.Icon;
  return (
    <div className={`w-9 h-9 flex items-center justify-center border ${meta.border} ${meta.bg} shrink-0`} title={meta.label}>
      <Icon size={16} className={meta.color} strokeWidth={1.75} />
    </div>
  );
}

function EvidenceFilesPanel({ requirement, canEdit }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // 0..100 upload phase
  const [uploadPhase, setUploadPhase] = useState(""); // "reading" | "uploading" | ""
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null); // { filename, content_type, data_url }
  const [thumbnails, setThumbnails] = useState({}); // { fileId: dataUrl }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/requirements/${requirement.id}/evidence`);
      setFiles(data || []);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [requirement.id]);

  // Lazy-load thumbnails for image files
  useEffect(() => {
    const imgFiles = files.filter((f) => {
      const ct = (f.content_type || "").toLowerCase();
      const ext = (f.filename.split(".").pop() || "").toLowerCase();
      return (ct.startsWith("image/") || ["png","jpg","jpeg","gif","webp","bmp"].includes(ext))
        && !thumbnails[f.id]
        && (f.size_bytes || 0) < 3 * 1024 * 1024; // skip auto-thumbnail for very large images
    });
    imgFiles.forEach(async (f) => {
      try {
        const { data } = await api.get(`/requirements/${requirement.id}/evidence/${f.id}/download`);
        setThumbnails((prev) => ({ ...prev, [f.id]: `data:${data.content_type};base64,${data.data_base64}` }));
      } catch { /* ignore */ }
    });
    /* eslint-disable-next-line */
  }, [files]);

  const handleUpload = async (fileObj) => {
    if (!fileObj) return;
    setErr("");
    if (fileObj.size > MAX_UPLOAD_BYTES) {
      setErr(`Dosya 25 MB sınırını aşıyor (${humanSize(fileObj.size)}).`);
      return;
    }
    setUploading(true);
    setUploadPhase("reading");
    setUploadProgress(0);
    try {
      // Phase 1: read file → base64
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onprogress = (ev) => {
          if (ev.lengthComputable) {
            const pct = Math.round((ev.loaded / ev.total) * 100);
            setUploadProgress(Math.min(pct, 99));
          }
        };
        reader.onload = () => {
          const res = reader.result || "";
          const idx = res.indexOf(",");
          resolve(idx >= 0 ? res.slice(idx + 1) : res);
        };
        reader.onerror = reject;
        reader.readAsDataURL(fileObj);
      });

      // Phase 2: POST with axios upload progress
      setUploadPhase("uploading");
      setUploadProgress(0);
      await api.post(
        `/requirements/${requirement.id}/evidence`,
        {
          filename: fileObj.name,
          content_type: fileObj.type || "application/octet-stream",
          data_base64: b64,
        },
        {
          onUploadProgress: (ev) => {
            if (ev.total) {
              const pct = Math.round((ev.loaded / ev.total) * 100);
              setUploadProgress(Math.min(pct, 99));
            }
          },
        }
      );
      setUploadProgress(100);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally {
      setUploading(false);
      setUploadPhase("");
      setUploadProgress(0);
    }
  };

  const handleDownload = async (file) => {
    try {
      const { data } = await api.get(`/requirements/${requirement.id}/evidence/${file.id}/download`);
      const link = document.createElement("a");
      link.href = `data:${data.content_type};base64,${data.data_base64}`;
      link.download = data.filename || "evidence";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const handlePreview = async (file) => {
    // Only images and PDFs are inline-previewable
    const ct = (file.content_type || "").toLowerCase();
    const ext = (file.filename.split(".").pop() || "").toLowerCase();
    const previewable = ct.startsWith("image/") || ct === "application/pdf" || ["png","jpg","jpeg","gif","webp","bmp","pdf"].includes(ext);
    if (!previewable) { handleDownload(file); return; }
    try {
      const { data } = await api.get(`/requirements/${requirement.id}/evidence/${file.id}/download`);
      setPreview({
        filename: data.filename,
        content_type: data.content_type,
        data_url: `data:${data.content_type};base64,${data.data_base64}`,
      });
    } catch (e) { setErr(formatApiError(e)); }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`"${file.filename}" dosyasını silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/requirements/${requirement.id}/evidence/${file.id}`);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const progressLabel = uploadPhase === "reading" ? "Okunuyor" : uploadPhase === "uploading" ? "Yükleniyor" : "İşleniyor";

  return (
    <div data-testid="evidence-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500">Evidence Files ({files.length})</div>
        {canEdit && (
          <label className="btn btn-ghost btn-sm cursor-pointer" data-testid="evidence-upload-btn">
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            <span className="ml-1">{uploading ? `${progressLabel}...` : "Yükle"}</span>
            <input
              type="file"
              className="hidden"
              disabled={uploading}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleUpload(f); }}
              data-testid="evidence-file-input"
            />
          </label>
        )}
      </div>

      {uploading && (
        <div className="mb-2 border border-[#00E5FF]/30 bg-[#00E5FF]/5 px-3 py-2" data-testid="evidence-progress">
          <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.15em] text-slate-400 mb-1">
            <span>{progressLabel}</span>
            <span data-testid="evidence-progress-value">{uploadProgress}%</span>
          </div>
          <div className="h-1.5 bg-slate-800/70 overflow-hidden">
            <div
              className="h-full bg-[#00E5FF] transition-all duration-150"
              style={{ width: `${uploadProgress}%` }}
              data-testid="evidence-progress-bar"
            />
          </div>
        </div>
      )}

      {err && (
        <div className="text-xs text-red-400 border border-red-500/20 bg-red-500/5 px-3 py-2 font-mono mb-2" data-testid="evidence-error">
          {err}
        </div>
      )}

      {loading ? (
        <div className="text-xs text-slate-500 font-mono">Yükleniyor...</div>
      ) : files.length === 0 ? (
        <div className="text-xs text-slate-500 border border-dashed border-slate-800/70 px-3 py-4 text-center" data-testid="evidence-empty">
          Henüz kanıt dosyası yüklenmedi. Maks 25 MB — PDF, PNG, JPG, DOCX, ZIP vb.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="evidence-list">
          {files.map((f) => {
            const thumb = thumbnails[f.id];
            return (
              <div key={f.id} className="flex items-center gap-3 border border-slate-800/70 bg-slate-900/40 px-3 py-2" data-testid={`evidence-item-${f.id}`}>
                {thumb ? (
                  <button
                    onClick={() => handlePreview(f)}
                    className="w-9 h-9 shrink-0 border border-slate-800/70 bg-slate-900 overflow-hidden hover:border-[#00E5FF]/60 transition"
                    data-testid={`evidence-thumb-${f.id}`}
                    title="Önizle"
                  >
                    <img src={thumb} alt="" className="w-full h-full object-cover" />
                  </button>
                ) : (
                  <FileTypeBadge filename={f.filename} contentType={f.content_type} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-100 truncate">{f.filename}</div>
                  <div className="text-[10px] font-mono text-slate-500">{humanSize(f.size_bytes)} · {f.uploaded_by_name || "—"} · {(f.uploaded_at || "").slice(0, 10)}</div>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => handlePreview(f)} data-testid={`evidence-preview-${f.id}`} title="Önizle">
                  <Eye size={12} />
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => handleDownload(f)} data-testid={`evidence-download-${f.id}`} title="İndir">
                  <Download size={12} />
                </button>
                {canEdit && (
                  <button className="btn btn-ghost btn-sm text-red-400 hover:text-red-300" onClick={() => handleDelete(f)} data-testid={`evidence-delete-${f.id}`} title="Sil">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {preview && <EvidencePreviewModal preview={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function EvidencePreviewModal({ preview, onClose }) {
  const isImage = (preview.content_type || "").startsWith("image/");
  const isPdf = preview.content_type === "application/pdf";
  return (
    <>
      <div className="fixed inset-0 bg-black/70 z-[100]" onClick={onClose} data-testid="evidence-preview-backdrop" />
      <div className="fixed inset-6 z-[101] bg-[#0F172A] border border-slate-800 flex flex-col" data-testid="evidence-preview-modal">
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
          <div className="text-sm font-medium text-slate-100 truncate">{preview.filename}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} data-testid="evidence-preview-close">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-auto flex items-center justify-center bg-slate-950/60 p-4">
          {isImage && <img src={preview.data_url} alt={preview.filename} className="max-w-full max-h-full object-contain" data-testid="evidence-preview-image" />}
          {isPdf && <iframe src={preview.data_url} title={preview.filename} className="w-full h-full bg-white" data-testid="evidence-preview-pdf" />}
          {!isImage && !isPdf && (
            <div className="text-sm text-slate-400 font-mono">Bu dosya tipi için önizleme yok — indirin.</div>
          )}
        </div>
      </div>
    </>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 mb-1">{label}</div>
      <div className={`text-xs ${mono ? "font-mono" : ""} text-slate-200 leading-relaxed`}>{value || "—"}</div>
    </div>
  );
}
